import {getTestProps} from '@benchling/js-test-utils';
import {
  ActionIcon,
  Group,
  TextInput,
  Tooltip,
  UnstyledButton,
  createStyles,
  getStylesRef,
} from '@mantine/core';
import React from 'react';

import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import InputChip from '../lib/InputChip';
import ValidationBadge from '../lib/ValidationBadge';
import type {EntitySearchTab} from './entity-search/EntitySearchModal';
import InputComponentsEditor from './InputComponentsEditor';
import {
  type ComposedCapabilities,
  getComponentLabel,
  getInputStatusForCapabilities,
  getInputStatusReasons,
  type InputNameError,
  MAX_INPUT_NAME_LENGTH,
  type PredictionComponent,
} from './newPredictionState';

// Expand · Name · Components · Status · Actions. The table shell is one grid; header and body rows
// use subgrid so every row shares the same column tracks.
export const INPUTS_GRID_COLUMNS = 'auto minmax(160px, 1fr) minmax(240px, 3fr) max-content 40px';

const INPUT_NAME_ERROR_MESSAGES: Record<InputNameError, string> = {
  blank: 'Every input needs a name',
  duplicate: 'Duplicate name',
  tooLong: `Names must be at most ${MAX_INPUT_NAME_LENGTH} characters`,
};

interface InputRowProps {
  capabilities: ComposedCapabilities;
  components: Array<PredictionComponent>;
  disabled?: boolean;
  expanded: boolean;
  // Resolved display name used for the row label and every aria-label in this row.
  name: string;
  nameError: InputNameError | null;
  nameValue: string;
  onBlur: () => void;
  onComponentsChange: (components: Array<PredictionComponent>) => void;
  onNameChange: (name: string) => void;
  onRemove: () => void;
  onSearchEntities?: (tab: EntitySearchTab) => void;
  onToggleExpand: () => void;
  // A row is "settled" once collapsed or blurred at least once. Until then we suppress the "Empty"
  // status so a freshly-added, still-being-edited row doesn't read as an error before the user types.
  settled: boolean;
}

export default function InputRow({
  capabilities,
  components,
  disabled = false,
  expanded,
  name,
  nameError,
  nameValue,
  onBlur,
  onComponentsChange,
  onNameChange,
  onRemove,
  onSearchEntities,
  onToggleExpand,
  settled,
}: InputRowProps): JSX.Element {
  const {classes, cx} = useStyles({expanded, hasNameError: nameError != null});
  const nameErrorMessage = nameError == null ? null : INPUT_NAME_ERROR_MESSAGES[nameError];

  const status = getInputStatusForCapabilities(components, capabilities);
  // Suppress only the "Empty" badge while unsettled; any other status is real feedback worth showing.
  const showStatus = settled || status !== 'empty';

  // Fire onBlur only when focus truly leaves the row (not when moving between the row's own controls),
  // so editing within the expanded panel doesn't prematurely mark the row settled.
  const handleBlur = (event: React.FocusEvent<HTMLDivElement>): void => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      onBlur();
    }
  };

  return (
    <div
      aria-label={name}
      className={cx(classes.rowGroup, {[classes.rowGroupExpanded]: expanded})}
      onBlur={handleBlur}
      role="group"
      {...getTestProps({component: 'InputRow', element: 'root'})}
    >
      <div className={classes.row}>
        <ActionIcon
          aria-label={expanded ? `Collapse ${name}` : `Edit ${name}`}
          className={classes.rowAction}
          onClick={onToggleExpand}
          variant="subtle"
        >
          <BenchlingIcon icon={expanded ? 'triangle-down' : 'triangle-right'} />
        </ActionIcon>
        {/* Sighted users get the red underline plus this tooltip; `focus: true` also opens it on
            keyboard focus (Mantine's default is hover-only). Assistive tech needs a non-visual
            channel, and Mantine hardcodes `aria-invalid`/`aria-describedby` from its own `error`
            prop *after* spreading caller props — so passing those by hand is silently overwritten.
            Instead we pass `error` and visually hide only the message slot: that wires up
            aria-invalid and points aria-describedby at the (clipped) message, without the visible
            text below the input that would break this row's subgrid alignment. */}
        <Tooltip
          disabled={nameErrorMessage == null}
          events={{focus: true, hover: true, touch: false}}
          label={nameErrorMessage ?? undefined}
        >
          <TextInput
            aria-label={`${name} name`}
            classNames={{error: classes.visuallyHiddenError, input: classes.nameInput}}
            disabled={disabled}
            error={nameErrorMessage}
            maxLength={MAX_INPUT_NAME_LENGTH}
            onChange={(event) => {
              onNameChange(event.currentTarget.value);
            }}
            size="lg"
            value={nameValue}
            variant="unstyled"
          />
        </Tooltip>
        {expanded ? (
          <Group className={classes.componentsCell} spacing="sm" />
        ) : (
          <UnstyledButton
            aria-label={`Expand input ${name} components`}
            className={classes.componentsButton}
            onClick={onToggleExpand}
            type="button"
          >
            <Group className={classes.componentsCell} spacing="sm">
              {components.map((component, componentIndex) => (
                <InputChip
                  key={component.id}
                  entityName={component.entity?.name}
                  // Inside the expand button; the expanded component cards offer focusable chips.
                  focusable={false}
                  label={getComponentLabel(components, componentIndex)}
                  size="md"
                  type={component.type}
                  value={component.value}
                />
              ))}
            </Group>
          </UnstyledButton>
        )}
        {/* The wrapper stays mounted even when the badge is suppressed: `row` is `display: contents`, so
            omitting the cell entirely would let the trailing action slide into the status column.
            The badge reports capability status only — name problems are surfaced on the name field
            above (underline + tooltip + aria-invalid), since names aren't capabilities. */}
        <div className={classes.statusCell}>
          {showStatus && (
            <ValidationBadge
              reasons={getInputStatusReasons(components, capabilities)}
              size="sm"
              status={status}
            />
          )}
        </div>
        <ActionIcon
          aria-label={`Remove ${name}`}
          className={classes.rowAction}
          disabled={disabled}
          onClick={onRemove}
          variant="subtle"
        >
          <BenchlingIcon icon="trash-can-outline" />
        </ActionIcon>
      </div>
      {expanded && (
        <div className={classes.expandedPanel}>
          <InputComponentsEditor
            capabilities={capabilities}
            components={components}
            disabled={disabled}
            onComponentsChange={onComponentsChange}
            onSearchEntities={onSearchEntities}
          />
        </div>
      )}
    </div>
  );
}

const useStyles = createStyles(
  (theme, {expanded, hasNameError}: {expanded: boolean; hasNameError: boolean}) => ({
    rowGroup: {
      [`&:hover .${getStylesRef('inputRowAction')}`]: {
        opacity: 1,
        pointerEvents: 'auto',
      },
      '&:not(:last-of-type)': {
        borderBottom: `1px solid ${theme.colors.gray[2]}`,
      },
      alignItems: 'center',
      display: 'grid',
      gridColumn: '1 / -1',
      gridTemplateColumns: 'subgrid',
      padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    },
    rowGroupExpanded: {
      // Halfway between page white and gray[0] — a hint of tint without the full gray band.
      backgroundColor: theme.fn.lighten(theme.colors.gray[0], 0.5),
    },
    row: {
      display: 'contents',
    },
    nameInput: {
      borderBottom: `1px solid ${hasNameError ? theme.colors.red[6] : 'transparent'}`,
      fontFamily: 'var(--font-mono, monospace)',
      fontWeight: 700,
      height: 'auto',
      minWidth: 0,
      minHeight: 0,
      overflow: 'hidden',
      padding: 0,
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      width: '100%',
      '&:hover': {
        borderBottomColor: hasNameError ? theme.colors.red[6] : theme.colors.gray[4],
      },
      '&:focus': {
        borderBottomColor: hasNameError ? theme.colors.red[6] : theme.colors.blue[6],
      },
    },
    // Mantine renders the `error` message below the input. We keep it in the a11y tree (it backs the
    // input's aria-describedby) but clip it visually: the message is shown as a tooltip instead, and
    // reserving vertical space for it would change the height of erroring rows only. `position:
    // absolute` also keeps it out of flow, so the row's subgrid doesn't auto-place it as an extra
    // column (`.row` is `display: contents`, so these cells are the grid's items).
    visuallyHiddenError: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      whiteSpace: 'nowrap',
      width: 1,
    },
    componentsCell: {
      minWidth: 0,
    },
    statusCell: {
      minWidth: 0,
    },
    componentsButton: {
      backgroundColor: 'transparent',
      border: 0,
      color: 'inherit',
      cursor: 'pointer',
      font: 'inherit',
      margin: 0,
      minWidth: 0,
      padding: 0,
      textAlign: 'left',
      width: '100%',
    },
    rowAction: {
      ref: getStylesRef('inputRowAction'),
      // Icons stay visible while the row is expanded; when collapsed they reveal only on row hover
      // (see `.rowGroup:hover` above) or keyboard focus. We key off the `expanded` prop rather than
      // `:focus-within` so a mouse click on the chevron doesn't leave the icons pinned after collapse.
      // `pointerEvents` is toggled with `opacity` so hidden icons don't capture clicks in the blank
      // action area (opacity alone leaves the button clickable); keyboard focus is unaffected.
      opacity: expanded ? 1 : 0,
      pointerEvents: expanded ? 'auto' : 'none',
      '&:focus-visible': {
        opacity: 1,
        pointerEvents: 'auto',
      },
    },
    expandedPanel: {
      gridColumn: '1 / -1',
      paddingBottom: theme.spacing.md,
      paddingTop: theme.spacing.xs,
    },
  })
);
